import { ArrowRightIcon, PulseIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BarChart } from "../components/ui/bar-chart";
import { Card, ErrorState, Loading, PageHeader, StatCard, Th } from "../components/ui/misc";
import {
  daysAgo,
  formatDateTime,
  formatDuration,
  formatNumber,
  isoDay,
  relativeTime,
} from "../lib/format";
import { analyticsOverviewQuery, dashboardQuery } from "../lib/queries";
import { useCan } from "../lib/session";

export function DashboardPage() {
  const { data, isPending, error, refetch } = useQuery(dashboardQuery);
  const canReadAnalytics = useCan("analytics.read");
  const week = useQuery({
    ...analyticsOverviewQuery({ from: daysAgo(6), to: isoDay(new Date()) }),
    enabled: canReadAnalytics,
  });

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (isPending || !data) return <Loading label="Reading today's numbers" />;

  return (
    <>
      <PageHeader
        title="Dashboard"
        description="What players are doing right now, and what the catalog is waiting on."
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Playing now"
          value={formatNumber(data.traffic.onlineNow)}
          hint={`${formatNumber(data.traffic.launchesToday)} launches today`}
          accent={data.traffic.onlineNow > 0}
        />
        <StatCard
          label="Views today"
          value={formatNumber(data.traffic.viewsToday)}
          hint="Game pages opened"
        />
        <StatCard
          label="Live games"
          value={formatNumber(data.catalog.publishedGames)}
          hint={`${formatNumber(data.catalog.draftGames)} still in draft`}
        />
        <StatCard
          label="Waiting to publish"
          value={formatNumber(data.catalog.changedSinceLastRelease)}
          tone={data.catalog.changedSinceLastRelease > 0 ? "warning" : undefined}
          hint={
            data.catalog.lastReleaseAt
              ? `Last release ${relativeTime(data.catalog.lastReleaseAt)}`
              : "Never published"
          }
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1.35fr_1fr]">
        <Card
          eyebrow="Traffic"
          title="Game launches"
          actions={
            <Link
              to="/analytics"
              className="text-caption text-muted hover:text-accent-text inline-flex items-center gap-1.5 transition-colors"
            >
              Full analytics <ArrowRightIcon />
            </Link>
          }
        >
          <BarChart
            points={data.traffic.launches7d.map((point) => ({
              label: point.day,
              value: point.launches,
            }))}
          />
        </Card>

        <Card
          eyebrow="Inbox"
          title="Player reports"
          actions={
            <Link
              to="/reports"
              className="text-caption text-muted hover:text-accent-text inline-flex items-center gap-1.5 transition-colors"
            >
              {formatNumber(data.feedback.newReports)} new <ArrowRightIcon />
            </Link>
          }
        >
          {data.recentReports.length === 0 ? (
            <div className="text-caption text-muted flex items-center gap-2 py-6">
              <PulseIcon className="text-accent size-4" /> Nothing reported. Players are getting
              through.
            </div>
          ) : (
            <ul className="flex flex-col gap-3">
              {data.recentReports.map((report) => (
                <li
                  key={report.id}
                  className="border-line/60 border-b pb-3 last:border-0 last:pb-0"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-ui text-ink font-medium">
                      {report.gameName ?? "General"}
                    </span>
                    <span className="text-caption text-faint tabular shrink-0">
                      {formatDateTime(report.createdAt)}
                    </span>
                  </div>
                  <p className="text-caption text-body mt-1 line-clamp-2">{report.description}</p>
                </li>
              ))}
            </ul>
          )}
          <p className="text-caption text-faint border-line/60 mt-4 border-t pt-3">
            {formatNumber(data.feedback.newContacts)} new contact message
            {data.feedback.newContacts === 1 ? "" : "s"} ·{" "}
            <Link to="/contact" className="hover:text-accent-text transition-colors">
              open inbox
            </Link>
          </p>
        </Card>
      </div>

      {canReadAnalytics && week.data && week.data.topGames.length > 0 && (
        <Card
          className="mt-4"
          eyebrow="Last 7 days"
          title="Most played"
          bodyClassName="p-0"
          actions={
            <Link
              to="/analytics"
              className="text-caption text-muted hover:text-accent-text inline-flex items-center gap-1.5 transition-colors"
            >
              All games <ArrowRightIcon />
            </Link>
          }
        >
          <table className="w-full border-collapse text-left">
            <thead className="bg-well">
              <tr className="border-line border-b">
                <Th>Game</Th>
                <Th className="text-right">Launches</Th>
                <Th className="text-right">Play time</Th>
                <Th className="text-right">Avg session</Th>
              </tr>
            </thead>
            <tbody>
              {week.data.topGames.slice(0, 6).map((game) => (
                <tr key={game.gameId} className="t-row border-line/60 border-b last:border-0">
                  <td className="h-9 px-3">
                    <Link
                      to="/analytics/games/$gameId"
                      params={{ gameId: String(game.gameId) }}
                      className="text-ink hover:text-accent-text transition-colors"
                    >
                      {game.name}
                    </Link>
                  </td>
                  <td className="tabular text-body h-9 px-3 text-right">
                    {formatNumber(game.launches)}
                  </td>
                  <td className="tabular text-body h-9 px-3 text-right">
                    {formatDuration(game.playSeconds)}
                  </td>
                  <td className="tabular text-muted h-9 px-3 text-right">
                    {formatDuration(game.avgSessionSeconds)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
